import React, { useEffect, useState } from 'react';
import { User, TrainingLog, TrainingStats } from '../types';
import { Activity, Plus, Trash2, Lock, Ruler, Timer, HeartPulse, Flame, Waves } from 'lucide-react';
import { api } from '../api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid } from 'recharts';

interface TrainingStatsSectionProps {
  user: User | null;
  onRequireLogin: () => void;
  refreshKey: number;
}

const STROKES = ['自由泳', '蛙泳', '仰泳', '蝶泳', '混合泳'];
// 本地日期（避免 UTC 时区导致凌晨打卡记到前一天）
function localToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
const COLORS = ['#0284c7', '#0ea5e9', '#10b981', '#f59e0b', '#8b5cf6'];

export const TrainingStatsSection: React.FC<TrainingStatsSectionProps> = ({ user, onRequireLogin, refreshKey }) => {
  const [logs, setLogs] = useState<TrainingLog[]>([]);
  const [stats, setStats] = useState<TrainingStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ logDate: localToday(), poolName: '', distanceMeters: '1000', durationMinutes: '30', strokeType: '自由泳', avgHeartRate: '', swolf: '', notes: '' });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    try {
      const data = await api<{ logs: TrainingLog[]; stats: TrainingStats }>('my_training');
      setLogs(data.logs);
      setStats(data.stats);
    } catch (ex: any) {
      setErr(ex.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [user?.id, refreshKey]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) { onRequireLogin(); return; }
    setErr('');
    setSaving(true);
    try {
      await api('log_training', {
        logDate: form.logDate, poolName: form.poolName.trim(), distanceMeters: parseInt(form.distanceMeters),
        durationMinutes: parseInt(form.durationMinutes), strokeType: form.strokeType,
        avgHeartRate: parseInt(form.avgHeartRate) || 0, swolf: parseInt(form.swolf) || 0, notes: form.notes.trim(),
      }, 'POST');
      setForm((f) => ({ ...f, poolName: '', notes: '' }));
      await load();
    } catch (ex: any) {
      setErr(ex.message);
    } finally {
      setSaving(false);
    }
  };

  const del = async (id: number) => {
    if (!confirm('删除该条训练记录？')) return;
    try { await api('delete_training', { id }); await load(); } catch (ex: any) { alert(ex.message); }
  };

  if (!user) {
    return (
      <section className="py-12 bg-white min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl border-2 border-dashed border-slate-200 p-16 text-center space-y-4">
            <div className="w-16 h-16 rounded-2xl bg-sky-100 text-sky-600 flex items-center justify-center mx-auto"><Lock className="w-8 h-8" /></div>
            <h2 className="text-xl font-black text-slate-900">个人训练看板需登录后使用</h2>
            <p className="text-sm text-slate-500">登录后即可打卡记录每次训练的距离、时长、心率与 SWOLF 效率指数，查看月度泳程趋势。</p>
            <button onClick={onRequireLogin} className="px-8 py-3 bg-sky-600 text-white text-sm font-bold rounded-xl shadow-lg shadow-sky-600/25">去登录 / 注册</button>
          </div>
        </div>
      </section>
    );
  }

  const monthlyData = stats ? Object.entries(stats.monthly).map(([m, v]) => ({ month: m.slice(5) + '月', distance: v })) : [];
  const strokeData = stats ? Object.entries(stats.by_stroke).map(([s, v]) => ({ name: s, value: v })) : [];

  return (
    <section className="py-12 bg-white min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        <div className="text-center">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-sky-100/80 text-sky-700 rounded-full text-xs font-bold mb-3">
            <Activity className="w-4 h-4" /><span>个人游泳训练数据看板</span>
          </div>
          <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight">打卡训练 · 见证成长</h2>
          <p className="text-slate-600 text-sm mt-2">记录每次训练，系统自动汇总距离、时长、SWOLF 划水效率与泳姿分布。</p>
        </div>

        {err && <p className="text-xs text-red-600 font-semibold bg-red-50 border border-red-200 rounded-xl px-3 py-2">{err}</p>}

        {/* 统计卡片 */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-gradient-to-br from-sky-600 to-blue-700 text-white rounded-2xl p-5 shadow-lg"><Ruler className="w-6 h-6 mb-2 opacity-80" /><div className="text-2xl font-black">{stats?.total_distance || 0}<span className="text-xs font-normal ml-1">米</span></div><div className="text-[11px] text-sky-200 mt-1">累计游程</div></div>
          <div className="bg-gradient-to-br from-cyan-600 to-sky-700 text-white rounded-2xl p-5 shadow-lg"><Timer className="w-6 h-6 mb-2 opacity-80" /><div className="text-2xl font-black">{stats?.total_minutes || 0}<span className="text-xs font-normal ml-1">分钟</span></div><div className="text-[11px] text-cyan-200 mt-1">累计训练时长</div></div>
          <div className="bg-gradient-to-br from-emerald-600 to-green-700 text-white rounded-2xl p-5 shadow-lg"><Waves className="w-6 h-6 mb-2 opacity-80" /><div className="text-2xl font-black">{stats?.sessions || 0}<span className="text-xs font-normal ml-1">次</span></div><div className="text-[11px] text-emerald-200 mt-1">训练次数</div></div>
          <div className="bg-gradient-to-br from-amber-500 to-orange-600 text-white rounded-2xl p-5 shadow-lg"><Activity className="w-6 h-6 mb-2 opacity-80" /><div className="text-2xl font-black">{stats?.avg_swolf || 0}</div><div className="text-[11px] text-amber-200 mt-1">平均 SWOLF（越低越好）</div></div>
          <div className="bg-gradient-to-br from-rose-500 to-red-600 text-white rounded-2xl p-5 shadow-lg"><Flame className="w-6 h-6 mb-2 opacity-80" /><div className="text-2xl font-black">{stats?.by_stroke ? Object.keys(stats.by_stroke).length : 0}<span className="text-xs font-normal ml-1">种</span></div><div className="text-[11px] text-rose-200 mt-1">涉及泳姿</div></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 打卡表单 */}
          <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6">
            <h3 className="text-base font-black text-slate-900 mb-4 flex items-center gap-2"><Plus className="w-4 h-4 text-sky-600" />新增训练打卡</h3>
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-bold text-slate-600 mb-1">训练日期</label><input type="date" required value={form.logDate} onChange={(e) => setForm({ ...form, logDate: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-xl bg-white text-sm" /></div>
                <div><label className="block text-xs font-bold text-slate-600 mb-1">主泳姿</label>
                  <select value={form.strokeType} onChange={(e) => setForm({ ...form, strokeType: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-xl bg-white text-sm">
                    {STROKES.map((s) => <option key={s}>{s}</option>)}
                  </select></div>
              </div>
              <div><label className="block text-xs font-bold text-slate-600 mb-1">泳池/水域</label><input type="text" required value={form.poolName} onChange={(e) => setForm({ ...form, poolName: e.target.value })} placeholder="如：长顺县全民健身中心游泳馆" className="w-full px-3 py-2 border border-slate-200 rounded-xl bg-white text-sm" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="block text-xs font-bold text-slate-600 mb-1">距离(米)</label><input type="number" min="1" required value={form.distanceMeters} onChange={(e) => setForm({ ...form, distanceMeters: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-xl bg-white text-sm" /></div>
                <div><label className="block text-xs font-bold text-slate-600 mb-1">时长(分钟)</label><input type="number" min="1" required value={form.durationMinutes} onChange={(e) => setForm({ ...form, durationMinutes: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-xl bg-white text-sm" /></div>
                <div><label className="block text-xs font-bold text-slate-600 mb-1">平均心率</label><input type="number" value={form.avgHeartRate} onChange={(e) => setForm({ ...form, avgHeartRate: e.target.value })} placeholder="可选" className="w-full px-3 py-2 border border-slate-200 rounded-xl bg-white text-sm" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-bold text-slate-600 mb-1">SWOLF 效率指数</label><input type="number" value={form.swolf} onChange={(e) => setForm({ ...form, swolf: e.target.value })} placeholder="可选，越低越好" className="w-full px-3 py-2 border border-slate-200 rounded-xl bg-white text-sm" /></div>
                <div><label className="block text-xs font-bold text-slate-600 mb-1">训练心得</label><input type="text" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="可选" className="w-full px-3 py-2 border border-slate-200 rounded-xl bg-white text-sm" /></div>
              </div>
              <button type="submit" disabled={saving} className="w-full py-3 bg-gradient-to-r from-sky-600 to-blue-600 text-white text-sm font-bold rounded-xl shadow disabled:opacity-60">
                {saving ? '保存中...' : '保存打卡记录'}
              </button>
            </form>
          </div>

          {/* 图表 */}
          <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6 space-y-6">
            <div>
              <h3 className="text-base font-black text-slate-900 mb-3">月度泳程趋势（米）</h3>
              {monthlyData.length ? (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="distance" fill="#0284c7" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <div className="text-center text-xs text-slate-400 py-10">暂无数据，先去打卡吧</div>}
            </div>
            <div>
              <h3 className="text-base font-black text-slate-900 mb-3">泳姿里程分布</h3>
              {strokeData.length ? (
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie data={strokeData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60} label={(p: any) => p.name}>
                      {strokeData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : <div className="text-center text-xs text-slate-400 py-10">暂无数据</div>}
            </div>
          </div>
        </div>

        {/* 记录列表 */}
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6">
          <h3 className="text-base font-black text-slate-900 mb-4">历史训练记录</h3>
          {loading ? <div className="text-center text-xs text-slate-400 py-8">加载中...</div> : logs.length === 0 ? (
            <div className="text-center text-xs text-slate-400 py-8">还没有训练记录，开始第一次打卡吧！</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead><tr className="text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3">日期</th><th className="py-2 pr-3">泳池</th><th className="py-2 pr-3">泳姿</th><th className="py-2 pr-3">距离</th><th className="py-2 pr-3">时长</th><th className="py-2 pr-3">心率</th><th className="py-2 pr-3">SWOLF</th><th className="py-2 pr-3">心得</th><th></th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {logs.map((l) => (
                    <tr key={l.id} className="text-slate-700">
                      <td className="py-2.5 pr-3 font-bold whitespace-nowrap">{l.log_date}</td>
                      <td className="py-2.5 pr-3">{l.pool_name}</td>
                      <td className="py-2.5 pr-3">{l.stroke_type}</td>
                      <td className="py-2.5 pr-3">{l.distance_meters}m</td>
                      <td className="py-2.5 pr-3">{l.duration_minutes}分钟</td>
                      <td className="py-2.5 pr-3">{l.avg_heart_rate || '-'}</td>
                      <td className="py-2.5 pr-3">{l.swolf || '-'}</td>
                      <td className="py-2.5 pr-3 max-w-[180px] truncate">{l.notes || '-'}</td>
                      <td className="py-2.5"><button onClick={() => del(l.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
